import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Loader2,
  Mail,
  MessageSquare,
  Paperclip,
  Send,
} from 'lucide-react';
import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { TEXT_REPLY_MAX } from '@crm/contracts';
import { describeError } from '../../lib/api.ts';
import {
  api,
  type EmailMessage,
  type MessageConversation,
  type MessageRecordKind,
  type TextMessage,
} from '../../lib/client.ts';
import { cn } from '../../lib/utils.ts';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { EmptyState } from '../ui/empty-state.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs.tsx';
import { Textarea } from '../ui/input.tsx';

/**
 * A record's Message Hub texts and emails (TS-1898, step 3 of TS-1891; plan
 * `.claude/plans/messages.md`), over the TS-1897 routes. Texts are conversations (one per client
 * number and company line), then a conversation's thread as bubbles; emails are one list, each
 * opening to its plain-text body. Everything is matched by the record's contacts' phones and
 * emails when read, so a contact added later shows its past messages at once.
 *
 * Times are New York's, as every team-facing time in the CRM. Media is not in the copy (Twilio
 * media needs Twilio's credentials), so an attachment reads "open in Message Hub".
 */
const TIME = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  dateStyle: 'medium',
  timeStyle: 'short',
});
const when = (iso: string | null) => (iso ? TIME.format(new Date(iso)) : '—');

const CHANNEL: Record<string, string> = {
  // A reply sent from the CRM before Message Hub copies it back: Twilio picks RCS or SMS.
  text: 'Text',
  sms: 'SMS',
  mms: 'MMS',
  whatsapp: 'WhatsApp',
  rcs: 'RCS',
};
const channelLabel = (c: string) => CHANNEL[c.toLowerCase()] ?? c.toUpperCase();

/** "people" for a contact, "this project's contacts" for a project. */
const WHO: Record<MessageRecordKind, string> = {
  projects: "this project's contacts",
  contacts: 'this contact',
};

export function RecordMessages({
  kind,
  id,
  contactNames,
  openConversation,
}: {
  kind: MessageRecordKind;
  id: string;
  /** The record's contacts' names by id, so a conversation reads "with Maria Lopez". */
  contactNames: ReadonlyMap<string, string>;
  /** A conversation to open first (Home's Pending messages). */
  openConversation?: string | undefined;
}) {
  const [tab, setTab] = useState<'texts' | 'emails'>('texts');
  const conversations = useQuery({
    queryKey: ['messages', kind, id, 'conversations'],
    queryFn: () => api.messages.conversations(kind, id),
  });
  return (
    <Tabs
      value={tab}
      onValueChange={(v) => setTab(v as 'texts' | 'emails')}
      data-testid="record-messages"
    >
      <TabsList>
        <TabsTrigger value="texts">
          <MessageSquare className="size-4" aria-hidden /> Texts
          {conversations.data ? ` (${conversations.data.items.length})` : ''}
        </TabsTrigger>
        <TabsTrigger value="emails">
          <Mail className="size-4" aria-hidden /> Emails
        </TabsTrigger>
      </TabsList>
      <TabsContent value="texts" className="mt-4">
        {conversations.isPending ? (
          <Loading />
        ) : conversations.error ? (
          <Failed error={conversations.error} />
        ) : (
          <Texts
            kind={kind}
            id={id}
            items={conversations.data.items}
            withoutPhone={conversations.data.contactsWithoutPhone}
            replyUnavailable={conversations.data.replyUnavailable}
            contactNames={contactNames}
            initialOpen={openConversation}
          />
        )}
      </TabsContent>
      <TabsContent value="emails" className="mt-4">
        {tab === 'emails' && (
          <Emails
            kind={kind}
            id={id}
            withoutEmail={conversations.data?.contactsWithoutEmail ?? 0}
            contactNames={contactNames}
          />
        )}
      </TabsContent>
    </Tabs>
  );
}

function Loading() {
  return (
    <p className="text-muted-foreground flex items-center gap-2 text-sm">
      <Loader2 className="size-4 animate-spin" aria-hidden /> Loading…
    </p>
  );
}

function Failed({ error }: { error: unknown }) {
  return (
    <p className="text-danger text-sm" role="alert">
      {describeError(error)}
    </p>
  );
}

/** "2 contacts have no phone number, so their texts cannot be found." */
function missingNote(count: number, what: 'phone number' | 'email address', things: string) {
  if (count === 0) return null;
  return `${count} ${count === 1 ? 'contact has' : 'contacts have'} no ${what}, so ${count === 1 ? 'its' : 'their'} ${things} cannot be found.`;
}

const names = (ids: readonly string[], contactNames: ReadonlyMap<string, string>) =>
  ids
    .map((c) => contactNames.get(c))
    .filter((n): n is string => !!n)
    .join(', ');

function Texts({
  kind,
  id,
  items,
  withoutPhone,
  replyUnavailable,
  contactNames,
  initialOpen,
}: {
  kind: MessageRecordKind;
  id: string;
  items: MessageConversation[];
  withoutPhone: number;
  replyUnavailable: string | null;
  contactNames: ReadonlyMap<string, string>;
  initialOpen?: string | undefined;
}) {
  // Until the user picks one, the requested conversation opens as soon as a refetch lists it.
  const [chosen, setOpen] = useState<string | null | undefined>(undefined);
  const open =
    chosen !== undefined
      ? chosen
      : initialOpen && items.some((c) => c.key === initialOpen)
        ? initialOpen
        : null;
  const note = missingNote(withoutPhone, 'phone number', 'texts');
  if (items.length === 0)
    return (
      <EmptyState
        icon={MessageSquare}
        title={`No texts with ${WHO[kind]}`}
        description={
          note ?? 'Texts sent or received through Message Hub show here once they are copied.'
        }
      />
    );
  const current = items.find((c) => c.key === open) ?? null;
  return (
    <div className="@container flex flex-col gap-2">
      {note && <p className="text-muted-foreground text-xs">{note}</p>}
      <div className="grid gap-4 @2xl:grid-cols-[18rem_minmax(0,1fr)]">
        <ul
          className={cn('flex flex-col gap-2', current && 'hidden @2xl:flex')}
          aria-label="Conversations"
        >
          {items.map((c) => (
            <li key={c.key}>
              <button
                type="button"
                onClick={() => setOpen(c.key)}
                aria-current={c.key === open}
                data-testid="conversation"
                className={cn(
                  'border-border bg-card hover:bg-muted w-full rounded-xl border p-3 text-left text-sm',
                  c.key === open && 'ring-primary ring-2',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="min-w-0 truncate font-medium">
                    {names(c.contactIds, contactNames) || c.clientNumber || 'Unknown number'}
                  </span>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {when(c.lastMessageAt)}
                  </span>
                </div>
                <p className="text-muted-foreground truncate text-xs">
                  {c.clientNumber} · via {c.ourNumberName ?? c.ourNumber ?? 'unknown line'}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  {c.channels.map((ch) => (
                    <Badge key={ch} variant="secondary">
                      {channelLabel(ch)}
                    </Badge>
                  ))}
                  <span className="text-muted-foreground text-xs">{c.messageCount} messages</span>
                </div>
                {c.lastMessagePreview && (
                  <p className="mt-1 line-clamp-2 text-xs">
                    {c.lastDirection === 'outbound' ? 'You: ' : ''}
                    {c.lastMessagePreview}
                  </p>
                )}
              </button>
            </li>
          ))}
        </ul>
        {current ? (
          <Thread
            // One thread (and its reply draft) per conversation: switching conversation must
            // never carry a draft typed for one person over to another (Hyller, PR #150).
            key={current.key}
            kind={kind}
            id={id}
            conversation={current}
            title={names(current.contactIds, contactNames) || current.clientNumber || ''}
            replyUnavailable={replyUnavailable}
            onBack={() => setOpen(null)}
          />
        ) : (
          <p className="text-muted-foreground hidden self-center text-center text-sm @2xl:block">
            Pick a conversation to read it.
          </p>
        )}
      </div>
    </div>
  );
}

function Thread({
  kind,
  id,
  conversation,
  title,
  replyUnavailable,
  onBack,
}: {
  kind: MessageRecordKind;
  id: string;
  conversation: MessageConversation;
  title: string;
  replyUnavailable: string | null;
  onBack: () => void;
}) {
  const q = useInfiniteQuery({
    queryKey: ['messages', kind, id, 'texts', conversation.key],
    queryFn: ({ pageParam }) =>
      api.messages.texts(kind, id, conversation.key, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  // Newest first from the API; a chat reads oldest at the top.
  const items = (q.data?.pages.flatMap((p) => p.items) ?? []).slice().reverse();
  return (
    <section
      className="border-border bg-card flex min-w-0 flex-col gap-3 rounded-xl border p-3"
      aria-label={`Conversation with ${title}`}
      data-testid="thread"
    >
      <header className="flex items-center gap-2">
        <Button variant="ghost" size="sm" className="@2xl:hidden" onClick={onBack}>
          <ArrowLeft className="size-4" aria-hidden /> Back
        </Button>
        <div className="min-w-0">
          <p className="truncate font-medium">{title}</p>
          <p className="text-muted-foreground truncate text-xs">
            {conversation.clientNumber} · via{' '}
            {conversation.ourNumberName ?? conversation.ourNumber ?? 'unknown line'}
          </p>
        </div>
      </header>
      {q.isPending ? (
        <Loading />
      ) : q.error ? (
        <Failed error={q.error} />
      ) : (
        <>
          {q.hasNextPage && (
            <Button
              variant="outline"
              size="sm"
              className="self-center"
              onClick={() => void q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
            >
              {q.isFetchingNextPage ? 'Loading…' : 'Load older messages'}
            </Button>
          )}
          <ol className="flex flex-col gap-2">
            {items.map((m) => (
              <Bubble key={m.id} m={m} />
            ))}
          </ol>
        </>
      )}
      {conversation.canReply ? (
        <ReplyBox kind={kind} id={id} conversation={conversation} />
      ) : (
        replyUnavailable && (
          <p className="text-muted-foreground text-xs" data-testid="reply-unavailable">
            {replyUnavailable}
          </p>
        )
      )}
    </section>
  );
}

/**
 * Reply through Twilio (TS-1904). The client receives the sender's first and last name on the
 * first line, then the text (SMS and RCS have no bold, so the name is its own line). Enter sends,
 * Shift+Enter is a new line. A refusal (an opted-out number…) is shown here and stays in the
 * thread as a failed message.
 */
function ReplyBox({
  kind,
  id,
  conversation,
}: {
  kind: MessageRecordKind;
  id: string;
  conversation: MessageConversation;
}) {
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  // Twilio did not confirm the last send (202): it may have gone out.
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: (text: string) => api.messages.reply(kind, id, conversation.key, text),
    onMutate: () => setUnconfirmed(null),
    onSuccess: (res) => {
      setBody('');
      if (res.message.status === 'unknown') setUnconfirmed(res.message.errorMessage);
    },
    // Sent or refused, the thread has a new line to show.
    onSettled: () => void qc.invalidateQueries({ queryKey: ['messages', kind, id] }),
  });
  const text = body.trim();
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (text && !send.isPending) send.mutate(text);
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) submit(e);
  };
  return (
    <form
      onSubmit={submit}
      className="border-border flex flex-col gap-2 border-t pt-3"
      data-testid="reply-box"
    >
      <Textarea
        aria-label="Reply"
        placeholder={`Text ${conversation.clientNumber ?? 'this number'}`}
        value={body}
        maxLength={TEXT_REPLY_MAX}
        onChange={(e) => {
          setBody(e.target.value);
          if (send.isError) send.reset();
        }}
        onKeyDown={onKey}
        className="max-h-32 min-h-16"
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground text-xs">
          Sent from Remote Leverage through Twilio, signed with your name.
        </p>
        <Button type="submit" size="sm" disabled={!text || send.isPending}>
          {send.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Send className="size-4" aria-hidden />
          )}
          Send
        </Button>
      </div>
      {send.isError && (
        <p className="text-danger text-xs" role="alert">
          {describeError(send.error)}
        </p>
      )}
      {unconfirmed && (
        <p className="text-xs text-[var(--crm-caution-fg)]" role="status">
          {unconfirmed}
        </p>
      )}
    </form>
  );
}

function Bubble({ m }: { m: TextMessage }) {
  const out = m.direction === 'outbound';
  // A CRM reply Twilio did not confirm may have gone out: never shown as "not delivered".
  const unconfirmed = m.sentFromCrm && m.status === 'unknown';
  const failed =
    !unconfirmed && (!!m.errorCode || !!m.errorMessage || /fail|undeliver/i.test(m.status ?? ''));
  return (
    <li
      className={cn('flex flex-col gap-1', out ? 'items-end' : 'items-start')}
      data-testid="text-message"
      data-direction={m.direction}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3 py-2 text-sm break-words whitespace-pre-wrap',
          out ? 'bg-primary text-primary-foreground' : 'bg-muted',
        )}
      >
        {m.body ?? <span className="italic opacity-80">(no text)</span>}
        {!!m.numMedia && (
          <p className="mt-1 flex items-center gap-1 text-xs opacity-90">
            <Paperclip className="size-3" aria-hidden /> {m.numMedia}{' '}
            {m.numMedia === 1 ? 'attachment' : 'attachments'}, open in Message Hub
          </p>
        )}
      </div>
      <p className="text-muted-foreground flex flex-wrap items-center gap-1 text-xs">
        {out && m.senderName ? `${m.senderName} · ` : ''}
        {when(m.sentAt)} · {channelLabel(m.channel)}
        {m.status && !unconfirmed ? ` · ${m.status}` : ''}
        {m.sentFromCrm && !failed && !unconfirmed
          ? ' · sent from ReAct, waiting for Message Hub'
          : ''}
      </p>
      {unconfirmed && (
        <p className="text-muted-foreground text-xs" role="note">
          Not confirmed by Twilio{m.errorMessage ? `: ${m.errorMessage}` : ''}
        </p>
      )}
      {failed && (
        <p className="text-danger text-xs" role="note">
          Not delivered{m.errorCode ? ` (error ${m.errorCode})` : ''}
          {m.errorMessage ? `: ${m.errorMessage}` : ''}
        </p>
      )}
    </li>
  );
}

function Emails({
  kind,
  id,
  withoutEmail,
  contactNames,
}: {
  kind: MessageRecordKind;
  id: string;
  withoutEmail: number;
  contactNames: ReadonlyMap<string, string>;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const q = useInfiniteQuery({
    queryKey: ['messages', kind, id, 'emails'],
    queryFn: ({ pageParam }) => api.messages.emails(kind, id, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  if (q.isPending) return <Loading />;
  if (q.error) return <Failed error={q.error} />;
  const items = q.data.pages.flatMap((p) => p.items);
  const note = missingNote(withoutEmail, 'email address', 'emails');
  if (items.length === 0)
    return (
      <EmptyState
        icon={Mail}
        title={`No emails with ${WHO[kind]}`}
        description={note ?? 'Emails with clients show here once Message Hub has copied them.'}
      />
    );
  return (
    <div className="flex flex-col gap-2">
      {note && <p className="text-muted-foreground text-xs">{note}</p>}
      <ul className="flex flex-col gap-2" aria-label="Emails">
        {items.map((e) => (
          <EmailRow
            key={e.id}
            e={e}
            open={open === e.id}
            onToggle={() => setOpen(open === e.id ? null : e.id)}
            contactNames={contactNames}
          />
        ))}
      </ul>
      {q.hasNextPage && (
        <Button
          variant="outline"
          size="sm"
          className="self-center"
          onClick={() => void q.fetchNextPage()}
          disabled={q.isFetchingNextPage}
        >
          {q.isFetchingNextPage ? 'Loading…' : 'Load more emails'}
        </Button>
      )}
    </div>
  );
}

const person = (a: { address: string | null; name: string | null }) =>
  a.name && a.address ? `${a.name} <${a.address}>` : (a.name ?? a.address ?? '');

function EmailRow({
  e,
  open,
  onToggle,
  contactNames,
}: {
  e: EmailMessage;
  open: boolean;
  onToggle: () => void;
  contactNames: ReadonlyMap<string, string>;
}) {
  const from = e.fromName ?? e.fromAddress ?? 'Unknown sender';
  const to = e.to.map(person).join(', ');
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <li className="border-border bg-card rounded-xl border text-sm" data-testid="email">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="hover:bg-muted flex w-full items-start gap-2 rounded-xl p-3 text-left"
      >
        <Chevron className="mt-0.5 size-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span className="min-w-0 truncate font-medium">{e.subject || '(no subject)'}</span>
            <span className="text-muted-foreground shrink-0 text-xs">{when(e.sentAt)}</span>
          </div>
          <p className="text-muted-foreground truncate text-xs">
            {from} → {to || '—'}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <Badge variant={e.direction === 'outbound' ? 'brand' : 'secondary'}>
              {e.direction === 'outbound' ? 'Sent' : 'Received'}
            </Badge>
            {names(e.contactIds, contactNames) && (
              <span className="text-muted-foreground text-xs">
                with {names(e.contactIds, contactNames)}
              </span>
            )}
            {e.attachmentCount > 0 && (
              <span className="text-muted-foreground flex items-center gap-1 text-xs">
                <Paperclip className="size-3" aria-hidden /> {e.attachmentCount}
              </span>
            )}
          </div>
          {!open && e.snippet && <p className="mt-1 line-clamp-1 text-xs">{e.snippet}</p>}
        </div>
      </button>
      {open && (
        <div className="border-border flex flex-col gap-2 border-t p-3" data-testid="email-body">
          {e.cc.length > 0 && (
            <p className="text-muted-foreground text-xs">Cc: {e.cc.map(person).join(', ')}</p>
          )}
          <p className="break-words whitespace-pre-wrap">
            {e.bodyText ?? e.snippet ?? '(no text)'}
          </p>
          {e.bodyTruncated && (
            <p className="text-muted-foreground text-xs italic">
              This email is shortened here. The whole email is in Message Hub.
            </p>
          )}
          {e.attachmentCount > 0 && (
            <p className="text-muted-foreground text-xs">
              {e.attachmentCount} {e.attachmentCount === 1 ? 'attachment' : 'attachments'}, open in
              Message Hub
            </p>
          )}
        </div>
      )}
    </li>
  );
}
